import { Suspense } from 'react';
import type {
  CloudStorageProvider,
  IntegrationConnectionOutcome,
} from '@listening/api-contract/integration-providers';
import { cloudStorageImportSearch } from '@/lib/cloud-storage-import-search';
import { authClient } from '@/lib/auth-client';
import { CloudStorageImportReturn } from '@/components/web-fe/dialogs/cloud-storage-import-return';
import HomeIcon from '@listening/assets/icons/home.svg?react';
import {
  libraryViewSource,
  processingQueueSource,
} from '@listening/frontend/query/audio-lists';
import { foldersAtom } from '@listening/frontend/query/folders';
import { awaitWithinSsrBudget } from '@/lib/ssr-budget';
import { ApiBoundary } from '@/components/shared-ui/api-boundary';
import { getLibrarySortMode } from '@listening/frontend/stores/library-sort-store';
import { createFileRoute } from '@tanstack/react-router';
import { DashboardQueryError } from '@/components/shared-ui/dashboard-query-error';
import * as m from '@/paraglide/messages';
import { HomeV2 } from '@/components/web-fe/design-v2/home-v2';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import { LibrarySkeleton } from '@/components/web-fe/design-v2/skeletons';

const isConnectedReturn = ({
  integration,
  connection,
}: {
  integration?: CloudStorageProvider | undefined;
  connection?: IntegrationConnectionOutcome | undefined;
}) => integration !== undefined && connection === 'connected';

export const Route = createFileRoute('/app/_authenticated/_dashboard/home')({
  component: HomePage,
  validateSearch: cloudStorageImportSearch,
  // The post-connect session refresh must reach the browser's cookie jar, so a
  // return from a cloud-storage connect runs this route on the client.
  ssr: ({ search }) =>
    search.status === 'success' && isConnectedReturn(search.value)
      ? false
      : undefined,
  beforeLoad: async ({ search }) => {
    if (isConnectedReturn(search))
      await authClient.getSession({ query: { disableCookieCache: true } });
  },
  // Route-level skeleton so the home shell (upload row + "Your files" header +
  // card grid) shows during the loader wait, instead of the generic dashboard
  // skeleton — mirrors the sibling files/$folderId route.
  pendingComponent: HomePageSkeleton,
  pendingMs: 0,
  // Typed-error surface for this route's suspense reads (auto-resets on
  // navigation, retries via `router.invalidate()`); renders at the dashboard
  // Outlet so the chrome stays.
  errorComponent: DashboardQueryError,
  // Starts the page's reads and waits for them: a list's rows reach the store
  // only when its page arrives, so the server must have them before it sends
  // the page. A typed failure settles a read like a success, and the page
  // renders it. Past the server's budget, the page's reads wait instead and
  // the server streams the skeleton first. Client navigations show
  // `pendingComponent`.
  loader: async ({ context: { atoms } }) => {
    const library = {
      sortMode: getLibrarySortMode(),
      unfiledOnly: false,
    };
    const handles = {
      processing: atoms.prefetch(processingQueueSource),
      folders: atoms.prefetch(foldersAtom),
    };
    await awaitWithinSsrBudget(
      Promise.all([
        atoms.ensure(libraryViewSource, library),
        atoms.ensure(processingQueueSource),
        atoms.ensure(foldersAtom),
      ]),
    );
    return handles;
  },
  context: () => ({
    title: m.common_home(),
    icon: HomeIcon,
  }),
});

function HomePage() {
  const search = Route.useSearch();
  const handles = Route.useLoaderData();
  return (
    <DashboardPage>
      <ApiBoundary>
        <Suspense fallback={<LibrarySkeleton />}>
          <HomeV2 {...handles} />
        </Suspense>
      </ApiBoundary>
      <CloudStorageImportReturn {...search} />
    </DashboardPage>
  );
}

// Route pendingComponent: the static page shell. Renders the library skeleton
// ("Your library" header + card grid) inside the real `DashboardPage` so the
// home layout — with sidebar + top bar — is visible during the loader wait.
function HomePageSkeleton() {
  return (
    <DashboardPage>
      <LibrarySkeleton />
    </DashboardPage>
  );
}
